import React from "react";
import { createRoot } from "react-dom/client";
import ChatWidget from "./components/ChatWidget";
import "./styles.css";

// Default configuration
const defaultConfig = {
  position: "bottom-right",
  // WebSocket URL is determined automatically from server endpoint
  // websocketUrl: handled server-side for security
  title: "Customer Support",
  logoUrl: null,
  primaryColor: "#4F46E5",
  welcomeMessage: "Hi! How can I help you?",
  placeholder: "Type your message...",
  allowFileUpload: false, // Disabled by default as requested
  maxMessageLength: 1000,
  showTimestamp: true,
  enableTypingIndicator: true,
  autoOpen: false,
  showUnreadBadge: true,
  // New customization options
  size: "large", // small, medium, large, xlarge
  theme: "auto", // light, dark, auto
  headerTheme: "default", // default, primary, custom
  showThemeToggler: true,
  customTheme: {
    light: {
      primary: "#4F46E5",
      background: "#ffffff",
      surface: "#f8fafc",
      text: "#1e293b",
      border: "#e2e8f0",
    },
    dark: {
      primary: "#6366f1",
      background: "#1e293b",
      surface: "#334155",
      text: "#f1f5f9",
      border: "#475569",
    },
  },
};

// Function to load CSS dynamically
function loadChatbotCSS() {
  // Check if CSS is already loaded
  if (document.querySelector('link[href*="chatbot-widget.css"]')) {
    return;
  }

  // Get the base URL from the current script
  const scripts = document.querySelectorAll('script[src*="chatbot-widget.js"]');
  let baseUrl = "https://bot.cioafrica.co"; // fallback

  if (scripts.length > 0) {
    const scriptSrc = scripts[scripts.length - 1].src;
    baseUrl = scriptSrc.replace("/chatbot-widget.js", "");
  }

  // Create and inject CSS link
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.type = "text/css";
  link.href = `${baseUrl}/chatbot-widget.css`;

  // Add to head
  document.head.appendChild(link);

  console.log("Chatbot CSS loaded from:", link.href);
}

// Initialize the chatbot widget
function initChatbot(config = {}) {
  const mergedConfig = { ...defaultConfig, ...config };

  // Load CSS first
  loadChatbotCSS();

  // Create container element
  const container = document.createElement("div");
  container.id = "chatbot-widget-container";
  document.body.appendChild(container);

  // Render the widget
  const root = createRoot(container);
  root.render(React.createElement(ChatWidget, { config: mergedConfig }));

  return {
    destroy: () => {
      root.unmount();
      document.body.removeChild(container);
    },
    open: () => {
      const widget = document.querySelector("#chatbot-widget-container");
      if (widget) {
        widget.dispatchEvent(new CustomEvent("chatbot:open"));
      }
    },
    close: () => {
      const widget = document.querySelector("#chatbot-widget-container");
      if (widget) {
        widget.dispatchEvent(new CustomEvent("chatbot:close"));
      }
    },
  };
}

// Function to read data attributes from script tag
function getConfigFromDataAttributes() {
  const scripts = document.querySelectorAll('script[src*="chatbot-widget.js"]');
  if (scripts.length === 0) return null;

  const script = scripts[scripts.length - 1]; // Get the last matching script
  const dataset = script.dataset;

  if (Object.keys(dataset).length === 0) return null;

  const config = {};

  // Map data attributes to config properties
  if (dataset.position) config.position = dataset.position;
  if (dataset.title) config.title = dataset.title;
  if (dataset.primaryColor) config.primaryColor = dataset.primaryColor;
  if (dataset.welcomeMessage) config.welcomeMessage = dataset.welcomeMessage;
  if (dataset.logoUrl) config.logoUrl = dataset.logoUrl;
  if (dataset.placeholder) config.placeholder = dataset.placeholder;
  if (dataset.websocketUrl) config.websocketUrl = dataset.websocketUrl;
  if (dataset.size) config.size = dataset.size;
  if (dataset.theme) config.theme = dataset.theme;
  if (dataset.autoOpen) config.autoOpen = dataset.autoOpen === "true";
  if (dataset.allowFileUpload)
    config.allowFileUpload = dataset.allowFileUpload === "true";
  if (dataset.showTimestamp)
    config.showTimestamp = dataset.showTimestamp === "true";
  if (dataset.enableTypingIndicator)
    config.enableTypingIndicator = dataset.enableTypingIndicator === "true";
  if (dataset.showUnreadBadge)
    config.showUnreadBadge = dataset.showUnreadBadge === "true";
  if (dataset.showThemeToggler)
    config.showThemeToggler = dataset.showThemeToggler === "true";
  if (dataset.headerTheme) config.headerTheme = dataset.headerTheme;
  if (dataset.maxMessageLength)
    config.maxMessageLength = parseInt(dataset.maxMessageLength);

  return config;
}

// Auto-initialize function
function autoInitialize() {
  let config = null;

  // First, try to get config from data attributes
  config = getConfigFromDataAttributes();

  // If no data attributes, try window.ChatbotConfig
  if (!config && window.ChatbotConfig) {
    config = window.ChatbotConfig;
  }

  // Initialize if we have config
  if (config) {
    window.ChatbotWidget = initChatbot(config);
  }
}

// Auto-initialize when DOM is ready
if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInitialize);
  } else {
    // DOM is already loaded
    autoInitialize();
  }
}

// Export for manual initialization and UMD builds
if (typeof module !== "undefined" && module.exports) {
  module.exports = { initChatbot, ChatWidget };
}

// Global initialization function
if (typeof window !== "undefined") {
  window.initChatbot = initChatbot;
  // Also set it on the global ChatbotWidget object for UMD builds
  if (!window.ChatbotWidget) {
    window.ChatbotWidget = { initChatbot, ChatWidget };
  }
}

// Export for UMD build
export { initChatbot, ChatWidget };
